import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  DashboardChart,
  DataTable
} from '@tonic/shared/components/ui';
import { ArrowUpRight, ArrowDownRight, Download, Filter } from 'lucide-react';
import { useResaleFinancials } from '@/features/re-commerce/api/hooks';
import { formatCurrency, formatNumber, formatPercent } from '@/utils/format';

const columns = [
  {
    accessorKey: 'id',
    header: 'Transaction ID',
  },
  {
    accessorKey: 'date',
    header: 'Date',
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <Badge variant={row.original.type === 'Sale' ? 'success' : 'default'}>
        {row.original.type}
      </Badge>
    ),
  },
  {
    accessorKey: 'item',
    header: 'Item',
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => formatCurrency(row.original.amount),
  },
  {
    accessorKey: 'profit',
    header: 'Profit',
    cell: ({ row }) => formatCurrency(row.original.profit),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={row.original.status === 'Completed' ? 'success' : 'warning'}>
        {row.original.status}
      </Badge>
    ),
  },
];

export function ResaleFinancialsPage() {
  const { data: financialsData, isLoading } = useResaleFinancials();

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-48 bg-neutral-800 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-neutral-800 animate-pulse h-32" />
          ))}
        </div>
      </div>
    );
  }

  // Default financials data if not available
  const data = financialsData?.resaleFinancials || {
    metrics: {
      revenue: { value: 0, trend: 0, change: 0 },
      profit: { value: 0, trend: 0, change: 0 },
      margin: { value: 0, trend: 0, change: 0 },
      averageOrder: { value: 0, trend: 0, change: 0 }
    },
    revenueOverTime: [],
    revenueByCategory: [],
    transactions: []
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Financial Overview</h1>
          <p className="text-sm text-neutral-500">Track revenue, profits, and financial metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <Badge variant={data.metrics.revenue.trend > 0 ? 'success' : 'destructive'}>
              {data.metrics.revenue.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(data.metrics.revenue.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.metrics.revenue.value)}</div>
            <p className="text-xs text-neutral-500">vs. previous period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
            <Badge variant={data.metrics.profit.trend > 0 ? 'success' : 'destructive'}>
              {data.metrics.profit.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(data.metrics.profit.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.metrics.profit.value)}</div>
            <p className="text-xs text-neutral-500">vs. previous period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
            <Badge variant={data.metrics.margin.trend > 0 ? 'success' : 'destructive'}>
              {data.metrics.margin.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(data.metrics.margin.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercent(data.metrics.margin.value)}</div>
            <p className="text-xs text-neutral-500">vs. previous period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
            <Badge variant={data.metrics.averageOrder.trend > 0 ? 'success' : 'destructive'}>
              {data.metrics.averageOrder.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(data.metrics.averageOrder.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.metrics.averageOrder.value)}</div>
            <p className="text-xs text-neutral-500">vs. previous period</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={data.revenueOverTime}
              xField="date"
              yField="value"
              height={300}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Revenue by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="bar"
              data={data.revenueByCategory}
              xField="category"
              yField="value"
              height={300}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data.transactions}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}
